/**
 * Affordability calculator math.
 *
 * Pure functions, no side effects, no React. Easy to unit test, easy to
 * reuse on the server (for SEO meta) or the client.
 *
 * Assumptions (conservative, based on real DR pre-construction terms):
 *
 * - Down payment: 20% of purchase price at signing
 * - Construction-period monthly payments: 60% of price spread over 24 months
 * - Balloon at delivery: remaining 20%, typically refinanced with a DR
 *   non-resident mortgage (Banco Popular / Scotiabank / López de Haro,
 *   ~70% LTV, 8–10% USD, 25-yr term)
 *
 * Therefore the buyer's deployed-cash equation during construction is:
 *
 *   savings >= 0.20 * price                 → down payment cap
 *   monthly * 24 >= 0.60 * price           → monthly capacity cap
 *
 * Max affordable price = MIN of the two caps.
 *
 * Rental yield + CONFOTUR savings projections are conservative midpoints
 * of public DR market data (TheLatinvestor 2025, AirDNA averages).
 */

export const ASSUMPTIONS = {
  downPaymentPct: 0.2,
  monthlyDuringConstructionPct: 0.6,
  constructionMonths: 24,
  balloonPct: 0.2,
  // Conservative cap rate (annual rental income / property price)
  capRateAnnual: 0.075,
  // CONFOTUR exempts buyer from 1% annual property tax for 15 years
  confoturAnnualTaxRate: 0.01,
  confoturYears: 15,
  // Conservative annual appreciation (DR avg 6–11%; we use 7%)
  appreciationAnnual: 0.07,
} as const;

export interface AffordabilityInput {
  /** Cash currently saved, in USD. */
  savingsUsd: number;
  /** Monthly amount user can commit during construction, in USD. */
  monthlyUsd: number;
}

export interface AffordabilityResult {
  /** Maximum affordable purchase price (USD), conservative. */
  maxPriceUsd: number;
  /** Which constraint capped the price: savings or monthly capacity. */
  bindingConstraint: "savings" | "monthly";
  /** Down payment owed at signing. */
  downPaymentUsd: number;
  /** Monthly payment during construction. */
  monthlyDuringConstructionUsd: number;
  /** Balloon owed at delivery (typically refinanced with mortgage). */
  balloonAtDeliveryUsd: number;
  /** Estimated monthly rent at conservative cap rate. */
  estimatedMonthlyRentUsd: number;
  /** Estimated annual rent at conservative cap rate. */
  estimatedAnnualRentUsd: number;
  /** Projected total CONFOTUR property-tax savings over 15 years. */
  confoturSavings15yUsd: number;
  /** Projected property value after 5 years at 7% annual appreciation. */
  projectedValue5yUsd: number;
  /** Whether the affordable price falls under typical DR market entry. */
  belowMarketEntry: boolean;
}

const MIN_PRACTICAL_PRICE = 50_000;

export function calculateAffordability(
  input: AffordabilityInput
): AffordabilityResult {
  const savings = Math.max(0, input.savingsUsd);
  const monthly = Math.max(0, input.monthlyUsd);

  const priceFromSavings = savings / ASSUMPTIONS.downPaymentPct;
  const priceFromMonthly =
    (monthly * ASSUMPTIONS.constructionMonths) /
    ASSUMPTIONS.monthlyDuringConstructionPct;

  const maxPriceRaw = Math.min(priceFromSavings, priceFromMonthly);
  const bindingConstraint =
    priceFromSavings < priceFromMonthly ? "savings" : "monthly";

  // Round down to nearest $1,000 for a cleaner display.
  const maxPriceUsd = Math.floor(maxPriceRaw / 1000) * 1000;

  const downPaymentUsd = Math.round(maxPriceUsd * ASSUMPTIONS.downPaymentPct);
  const monthlyDuringConstructionUsd = Math.round(
    (maxPriceUsd * ASSUMPTIONS.monthlyDuringConstructionPct) /
      ASSUMPTIONS.constructionMonths
  );
  const balloonAtDeliveryUsd = Math.round(
    maxPriceUsd * ASSUMPTIONS.balloonPct
  );

  const estimatedAnnualRentUsd = Math.round(
    maxPriceUsd * ASSUMPTIONS.capRateAnnual
  );
  const estimatedMonthlyRentUsd = Math.round(estimatedAnnualRentUsd / 12);

  const confoturSavings15yUsd = Math.round(
    maxPriceUsd *
      ASSUMPTIONS.confoturAnnualTaxRate *
      ASSUMPTIONS.confoturYears
  );

  const projectedValue5yUsd = Math.round(
    maxPriceUsd * Math.pow(1 + ASSUMPTIONS.appreciationAnnual, 5)
  );

  return {
    maxPriceUsd,
    bindingConstraint,
    downPaymentUsd,
    monthlyDuringConstructionUsd,
    balloonAtDeliveryUsd,
    estimatedMonthlyRentUsd,
    estimatedAnnualRentUsd,
    confoturSavings15yUsd,
    projectedValue5yUsd,
    belowMarketEntry: maxPriceUsd < MIN_PRACTICAL_PRICE,
  };
}

/**
 * Examples of the kind of property a given price tier maps to in DR.
 * Used to make the abstract number feel concrete.
 */
export function describePropertyTier(priceUsd: number): {
  zone: string;
  type: string;
} {
  if (priceUsd < 50_000) {
    return {
      zone: "Santiago periferia o SD Este",
      type: "Estudio o 1BR pre-construcción",
    };
  }
  if (priceUsd < 90_000) {
    return {
      zone: "Santiago centro, Santo Domingo Este",
      type: "1BR pre-construcción CONFOTUR",
    };
  }
  if (priceUsd < 130_000) {
    return {
      zone: "Las Terrenas, Punta Cana base",
      type: "1BR turístico CONFOTUR",
    };
  }
  if (priceUsd < 180_000) {
    return {
      zone: "Punta Cana, Bávaro, Naco / Piantini",
      type: "1BR–2BR turístico o residencial premium",
    };
  }
  return {
    zone: "Cap Cana, Piantini, Las Terrenas premium",
    type: "2BR+ residencial o turístico de alta gama",
  };
}
